import * as Tone from 'tone';

/**
 * MasteringChain.js
 * Comprehensive Master Bus Processing.
 * Chain: Input -> EQ3 -> MultibandCompressor -> StereoWidener -> Limiter -> Output
 */
export class MasteringChain {
    constructor() {
        this.input = new Tone.Gain(1);
        this.output = new Tone.Gain(1);

        // 1. Components
        // Task 2: Pre-Master Gain (+6dB) to drive the Limiter hard
        this.preGain = new Tone.Gain(2.0); // +6dB

        // Bus Compressor ("The Breathing Compressor") - Replaces external hack
        // Task 3: Stable Glue Settings
        this.busComp = new Tone.Compressor({
            threshold: -24,
            ratio: 4, // Higher ratio for glue
            attack: 0.03, // 30ms punch
            release: 0.1
        });

        // EQ: Broad tone shaping
        this.eq = new Tone.EQ3({
            low: 0,
            mid: 0,
            high: 0,
            lowFrequency: 400,
            highFrequency: 2500
        });

        // Multiband Compressor: Glue and dynamics
        this.multiband = new Tone.MultibandCompressor({
            lowFrequency: 200,
            highFrequency: 2500,
            low: { threshold: -24, ratio: 1.5, attack: 0.03, release: 0.2 },
            mid: { threshold: -24, ratio: 1.5, attack: 0.03, release: 0.2 },
            high: { threshold: -24, ratio: 1.5, attack: 0.03, release: 0.2 }
        });

        // Stereo Widener: Spatial enhancement
        this.widener = new Tone.StereoWidener(0.5); // Default neutral-ish width

        // Makeup Gain: Drive the Limiter (+4dB)
        this.makeupGain = new Tone.Gain(1.58); // +4dB approx (10^(4/20) = 1.58)

        // Limiter: Final safety and loudness
        this.limiter = new Tone.Limiter(-1); // Ceiling at -1dB

        // 2. Build Chain: Input -> PreGain -> BusComp -> EQ -> Multiband -> Widener -> MakeupGain -> Limiter -> Output
        this.input.chain(this.preGain, this.busComp, this.eq, this.multiband, this.widener, this.makeupGain, this.limiter, this.output);
    }

    /**
     * Updates Compressor dynamics based on Active Density.
     * "The Breathing Compressor" - Stabilized
     * @param {number} density - 0.0 (Sparse) to 1.0 (Dense)
     */
    updateDynamics(density) {
        // Task 3: Stable Glue
        // Only modulate Threshold to control GR amount.
        // Lock Ratio/Attack to preserve transient character.

        const d = Math.max(0, Math.min(1, density));

        // Density increases -> Threshold rises (less compression)?
        // No, typically High Density = Needs more Glue (Lower Threshold).
        // BUT existing logic was: Low Density (-24) -> High Density (-10).
        // This implies "Let it breathe when busy"? Or "Don't overcompress loud wall of sound"?
        // Usually, mixing into a limiter:
        // Sparse: Gentle leveling.
        // Dense: Need to control peaks more?
        // Let's stick to the prompt's implication: "Modulate Threshold based on Density".
        // If we want "Glue", we usually compress 2-3dB.
        // Let's invert the previous logic to be more standard:
        // Sparse: Threshold -15 (Light touch)
        // Dense: Threshold -24 (Dig deeper to glue the wall)

        const targetThreshold = -15 - (d * 9); // -15 down to -24

        this.busComp.threshold.rampTo(targetThreshold, 1.0);
        // Ratio and Attack remain fixed as per Task 3
    }

    /**
     * Connects the chain to a destination (e.g., Master Output)
     */
    connect(destination) {
        this.output.connect(destination);
    }

    /**
     * Applies a mixing profile to the chain.
     * @param {Object} profile - { eq: {low, mid, high}, compression: {threshold, ratio...}, width: 0-1 }
     */
    configure(profile) {
        if (!profile) return;

        const rampTime = 0.5; // Smooth transitions

        // EQ
        if (profile.eq) {
            this.eq.low.rampTo(profile.eq.low ?? 0, rampTime);
            this.eq.mid.rampTo(profile.eq.mid ?? 0, rampTime);
            this.eq.high.rampTo(profile.eq.high ?? 0, rampTime);
        }

        // Compression (Applied globally to all bands for glue, unless bands specified)
        // We stick to the simplified "Glue" approach where the profile sets global dynamics char.
        if (profile.compression) {
            const bands = ['low', 'mid', 'high'];
            bands.forEach(band => {
                const comp = this.multiband[band];
                if (comp) {
                    if (profile.compression.threshold !== undefined) comp.threshold.value = profile.compression.threshold;
                    if (profile.compression.ratio !== undefined) comp.ratio.value = profile.compression.ratio;
                    if (profile.compression.attack !== undefined) comp.attack.value = profile.compression.attack;
                    if (profile.compression.release !== undefined) comp.release.value = profile.compression.release;
                }
            });
        }

        // Width
        if (profile.width !== undefined) {
             this.widener.width.rampTo(profile.width, rampTime);
        }
    }

    dispose() {
        this.input.dispose();
        this.busComp.dispose();
        this.eq.dispose();
        this.multiband.dispose();
        this.widener.dispose();
        this.limiter.dispose();
        this.output.dispose();
    }
}
